import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import BackButton from '../../components/back-button/BackButton';
import SortedTable from '../../components/sorted-table/SortedTable';
import AnimalBanner from '../../components/animal/AnimalBanner';
import ActionMenu from '../../components/action-buttons/ActionButtons';
import QuickView from '../../components/quick-view/QuickView';
import { DotLoader } from '../../components';
import MergeTransactionModal from '../../components/merge-transaction-modal/MergeTransactionModal';
import SpecimenLotInfo from '../../components/specimen-lot-info/SpecimenLotInfo';
import StripeKycDocumentModal from '../../components/account-settings/payout-info/StripeKycDocumentModal';
import StripePayoutReviewModal from '../../components/account-settings/payout-info/StripePayoutReviewModal';
import StripePayoutSetupModal from '../../components/account-settings/payout-info/StripePayoutSetupModal';
import { AppRoles } from '../../types/enums/appRoles.enum';
import { AccountRoles } from '../../types/enums/accountRoles.enum';
import { Specimen, SpecimenInventory } from '../../types/interfaces/specimen.interfaces';
import { StripeMarketplaceAccess, StripePayoutSetupMode, TransactionStatusEnum } from '../../types/enums';
import { BulkTransaction } from '../../types/interfaces';
import { getSpecimensByOwnership } from '../../api/specimensApi';
import { getCatalogQuantity } from '../../api/catalogApi';
import { getMarketplaceProfileExists } from '../../api/marketPlaceProfileApi';
import { getMarketPlaceSpecimenUnitsCount } from '../../api/marketPlaceSpecimenApi';
import { getStripeAccountStatus } from '../../api/stripeConnectApi';
import { getStripeMarketplaceAccess } from '../../services/stripeConnect.service';
import useCanManagePayouts from '../../hooks/useCanManagePayouts';
import { StripeAccountStatus } from '../../types/interfaces/stripeConnect.interfaces';
import { getAnimalOwnershipsByAccountId } from '../../api/animalOwnersApi';
import { getBulkTransactions } from '../../api/bulkTransactionApi';
import { PermissionService } from '../../services/permission.service';
import { showToast } from '../../services/toast.service';
import { INVENTORY_PATHS, ROUTE_PATHS } from '../../constants/routePaths';
import { toastMessages } from '../../constants/errorMessages';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, MARKETPLACE_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../constants/common';
import { pickInventoryListState } from './Inventory';

export type SpecimenRow = {
  availableQty: number;
  ownedQty: number;
  specimen: Specimen;
};

const InventoryItem = (): JSX.Element => {
  const { animalId } = useParams();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const currentLocation = routerLocation.pathname;
  const listNavigationState = pickInventoryListState(routerLocation.state);

  const navigateWithFilterPreset = (path: string, extraState: Record<string, unknown> = {}) => {
    navigate(path, { state: { ...listNavigationState, ...extraState } });
  };

  const goToInventoryList = () => {
    navigate(ROUTE_PATHS.APP_INVENTORY, { state: listNavigationState });
  };

  const { accountId } = useAppSelector(state => state.activeAccount);
  const canManagePayouts = useCanManagePayouts();
  const accountLocked = useAppSelector(state => state.activeAccount.locked);
  const accountRoles = useAppSelector(state => state.auth.accountContext.roles) as AccountRoles[];
  const activeAccount = useAppSelector(state => state.activeAccount);
  const appRoles = useAppSelector(state => state.auth.appContext.roles) as AppRoles[];
  const enabled = useAppSelector(state => state.user.enabled);
  const paymentStatus = useAppSelector(state => state.payment.addedPaymentMethod);
  const verified = useAppSelector(state => state.user.verified);

  const [catalogQuantity, setCatalogQuantity] = useState<number>();
  const [doesAccountHasAnimalOwnership, setDoesAccountHasAnimalOwnership] = useState<boolean>(false);
  const [isCheckingPendingWithdraw, setIsCheckingPendingWithdraw] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpenMergeTransactionModal, setIsOpenMergeTransactionModal] = useState<boolean>(false);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState<boolean>(false);
  const [marketplaceSpecimenCount, setMarketplaceSpecimenCount] = useState<number>();
  const [pendingTransactions, setPendingTransactions] = useState<BulkTransaction[]>([]);
  const [quantityOwned, setQuantityOwned] = useState<number>();
  const [specimen, setSpecimen] = useState<Specimen>();
  const [specimens, setSpecimens] = useState<SpecimenRow[]>();
  const [isAddToMarketplaceChecking, setIsAddToMarketplaceChecking] = useState<boolean>(false);
  const [isPayoutSetupModalOpen, setIsPayoutSetupModalOpen] = useState<boolean>(false);
  const [payoutSetupMode, setPayoutSetupMode] = useState<StripePayoutSetupMode>(StripePayoutSetupMode.Kyc);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState<boolean>(false);
  const [isPayoutReviewModalOpen, setIsPayoutReviewModalOpen] = useState<boolean>(false);
  const [stripeStatus, setStripeStatus] = useState<StripeAccountStatus | null>(null);

  const isFemale = specimens?.[0]?.specimen?.animal?.gender?.toLowerCase() === 'female';

  const permissionService = new PermissionService(accountRoles, appRoles, enabled, verified);

  const handleGetSpecimens = useCallback(async () => {
    if (animalId && accountId) {
      try {
        setIsLoading(true);
        const params = {
          include: 'SpecimenType, Animal, SpecimenCustomDataValue, EmbryoStraw',
          excludeStatuses: [TransactionStatusEnum.Pending, TransactionStatusEnum.Hold, TransactionStatusEnum.Processed],
        };
        const specs = await getSpecimensByOwnership(animalId, accountId, params);
        let owned = 0;

        const quants = await Promise.all(
          specs.data.map(async (spec: SpecimenInventory): Promise<any> => {
            owned += spec.quantityOwned;
            return { specimen: spec, availableQty: spec.quantityAvailable, ownedQty: spec.quantityOwned };
          }),
        );
        setQuantityOwned(owned);
        setSpecimens(quants);
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setIsLoading(false);
      }
    }
  }, [animalId, accountId]);

  const handleGetCatalogQuantity = useCallback(async () => {
    if (animalId && accountId && doesAccountHasAnimalOwnership) {
      try {
        const { data: catalogUnits } = await getCatalogQuantity(animalId);
        catalogUnits != undefined && setCatalogQuantity(catalogUnits);
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    }
  }, [doesAccountHasAnimalOwnership, animalId, accountId]);

  const handleGetAnimalOwnership = useCallback(async () => {
    try {
      const { data: animalOwnership } = await getAnimalOwnershipsByAccountId({ filter: `accountId eq ${accountId}` });
      const found = animalOwnership.some(animal => animal.animalId === Number(animalId));
      setDoesAccountHasAnimalOwnership(found);
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  }, [animalId, accountId]);

  const handleGetMarketplaceSpecimenCount = useCallback(async () => {
    if (animalId && accountId) {
      try {
        const { data } = await getMarketPlaceSpecimenUnitsCount({
          accountId,
          animalId: Number(animalId),
        });
        const count =
          typeof data === 'number'
            ? data
            : typeof data?.marketplaceSpecimenCount === 'number'
            ? data.marketplaceSpecimenCount
            : undefined;
        setMarketplaceSpecimenCount(count);
      } catch {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    }
  }, [animalId, accountId]);

  useEffect(() => {
    handleGetAnimalOwnership();
  }, [handleGetAnimalOwnership]);

  useEffect(() => {
    handleGetSpecimens();
  }, [handleGetSpecimens]);

  useEffect(() => {
    handleGetCatalogQuantity();
  }, [handleGetCatalogQuantity]);

  useEffect(() => {
    handleGetMarketplaceSpecimenCount();
  }, [handleGetMarketplaceSpecimenCount]);

  const onSell = () => {
    navigateWithFilterPreset(ROUTE_PATHS.APP_INVENTORY + '/' + animalId + '/' + INVENTORY_PATHS.SELL);
  };

  const onWithdraw = async () => {
    if (accountId) {
      try {
        setIsCheckingPendingWithdraw(true);
        const query = {
          include: 'InventoryTransactions.Specimen.Animal,ShippingOrderDetail',
          filter: `status eq ${TransactionStatusEnum.Pending} || status eq ${TransactionStatusEnum.Processed};`,
          sort: '^' + 'CreatedDatetime',
        };
        const { data } = await getBulkTransactions(query);
        const filteredData = data.filter(item => item.shippingOrderDetail !== null);
        if (filteredData.length > 0) {
          setPendingTransactions(filteredData);
          setIsOpenMergeTransactionModal(true);
        } else {
          navigateWithFilterPreset(ROUTE_PATHS.APP_INVENTORY + '/' + animalId + '/' + INVENTORY_PATHS.WITHDRAW);
        }
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setIsCheckingPendingWithdraw(false);
      }
    }
  };

  const onUse = () => {
    navigateWithFilterPreset(ROUTE_PATHS.APP_INVENTORY + '/' + animalId + '/' + INVENTORY_PATHS.USE);
  };

  const continueToMarketplace = async (prompts: { setup?: boolean; document?: boolean } = { setup: true, document: true }) => {
    if (!accountId || !animalId) return;
    const marketplaceNextPath = `${ROUTE_PATHS.APP_INVENTORY}/${animalId}/${INVENTORY_PATHS.ADD_TO_MARKETPLACE}`;
    const [stripeAccess, stripeStatusResponse] = await Promise.all([
      getStripeMarketplaceAccess(accountId),
      getStripeAccountStatus(accountId).catch(() => ({ data: null })),
    ]);
    setStripeStatus(stripeStatusResponse?.data ?? null);

    if (stripeAccess === StripeMarketplaceAccess.Setup) {
      setPayoutSetupMode(StripePayoutSetupMode.Kyc);
      if (prompts.setup !== false) setIsPayoutSetupModalOpen(true);
      return;
    }
    if (stripeAccess === StripeMarketplaceAccess.Restricted) {
      if (!canManagePayouts) {
        showToast.info(MARKETPLACE_CONSTANTS.STRIPE_PAYOUT_OWNER_ACTION_REQUIRED);
        return;
      }
      setPayoutSetupMode(StripePayoutSetupMode.Edit);
      setIsPayoutSetupModalOpen(true);
      return;
    }

    if (stripeAccess === StripeMarketplaceAccess.Document) {
      if (prompts.document !== false) setIsDocumentModalOpen(true);
      return;
    }

    if (stripeAccess === StripeMarketplaceAccess.Pending) {
      setIsPayoutReviewModalOpen(true);
      return;
    }

    const { data: profileExists } = await getMarketplaceProfileExists(accountId);
    if (profileExists === true) {
      navigateWithFilterPreset(marketplaceNextPath);
      return;
    }

    navigate(`${ROUTE_PATHS.APP_MARKETPLACE}/profile/${accountId}`, {
      state: {
        marketplaceOnboardingFlow: true,
        marketplaceNextPath,
        marketplaceBackPath: `${ROUTE_PATHS.APP_INVENTORY}/${animalId}`,
        ...listNavigationState,
      },
    });
  };

  const onAddToMarketplace = async () => {
    if (!accountId || !animalId) return;
    try {
      setIsAddToMarketplaceChecking(true);
      await continueToMarketplace();
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setIsAddToMarketplaceChecking(false);
    }
  };

  const onDiscard = () => {
    navigateWithFilterPreset(ROUTE_PATHS.APP_INVENTORY + '/' + animalId + '/' + INVENTORY_PATHS.DISCARD);
  };

  const handleQuickViewOpen = (item: Specimen) => {
    setSpecimen(item);
    setIsQuickViewOpen(true);
  };

  const getInventoryItems = (headers: string[], className: string, heading: string) => {
    return (
      <div className={className}>
        <h1>{heading}</h1>
        <SortedTable
          headers={headers.map((header: string) => {
            return { displayName: header };
          })}
          data={specimens?.map((spec: SpecimenRow) => {
            const flushMating =
              spec.specimen.specimenCustomDataValues?.find(val => val.specimenCustomDataKey?.keyName === 'Flush Mating')?.value ??
              '-';
            return className === 'mobile-view'
              ? [
                  { content: <SpecimenLotInfo specimenId={spec.specimen.specimenId} lotNumber={spec.specimen.lotNumber} /> },
                  { content: spec.availableQty },
                  { content: spec.ownedQty },
                  {
                    content: (
                      <ActionMenu
                        actionButtons={[{ name: BUTTON_CONSTANTS.QUICK_VIEW, action: () => handleQuickViewOpen(spec.specimen) }]}
                      />
                    ),
                  },
                ]
              : [
                  { content: spec.specimen.specimenType?.name },
                  { content: spec.specimen.specimenUOM },
                  {
                    content: (
                      <SpecimenLotInfo
                        specimenId={spec.specimen.specimenId}
                        lotNumber={spec.specimen.lotNumber}
                        freezeDate={spec.specimen.freezeDate}
                      />
                    ),
                  },
                  {
                    content: isFemale ? flushMating : spec.specimen.quality ?? '-',
                  },
                  { content: spec.availableQty },
                  { content: spec.ownedQty },
                  {
                    content: (
                      <ActionMenu
                        actionButtons={[{ name: BUTTON_CONSTANTS.QUICK_VIEW, action: () => handleQuickViewOpen(spec.specimen) }]}
                      />
                    ),
                  },
                ];
          })}
          loading={isLoading}
          noResultsMessage={LABEL_CONSTANTS.NO_INVENTORY}
        />
      </div>
    );
  };

  const checkValidity = () => {
    return (
      (paymentStatus != undefined &&
        (!paymentStatus ||
          activeAccount.billingAmount.outstandingBalanceCents > 0 ||
          activeAccount.billingAmount.penaltyCents > 0)) ||
      accountLocked
    );
  };

  const handleCreateNewOrder = () => {
    navigateWithFilterPreset(ROUTE_PATHS.APP_INVENTORY + '/' + animalId + '/' + INVENTORY_PATHS.WITHDRAW);
  };

  const handleUpdateOrder = (transactionId: number) => {
    navigate(ROUTE_PATHS.APP_UPDATE_WITHDRAW_TRANSACTION + transactionId, {
      state: { returnLocation: currentLocation, ...listNavigationState },
    });
  };

  return (
    <div className="inventory-item">
      <BackButton onClick={goToInventoryList} />

      <AnimalBanner animalId={animalId}>
        <div className="quantity">
          <h3>{LABEL_CONSTANTS.QUANTITY_OWNED}:</h3>
          <span> {quantityOwned ?? '--'} Units</span>
        </div>

        {doesAccountHasAnimalOwnership && (
          <div className="quantity">
            <h3>{LABEL_CONSTANTS.CATALOG_QUANTITY}:</h3>
            <span> {catalogQuantity ?? '--'} Units</span>
          </div>
        )}

        <div className="quantity">
          <h3>{LABEL_CONSTANTS.MARKETPLACE_SPECIMEN_QUANTITY}:</h3>
          <span> {marketplaceSpecimenCount ?? '--'} Units</span>
        </div>

        {permissionService.userHasInventoryPermission() ? (
          <div className="action-buttons">
            <button className="button medium blue" type="button" onClick={onSell} disabled={checkValidity()}>
              {BUTTON_CONSTANTS.SELL_TRANSFER}
            </button>
            <button className="button large blue inverted" type="button" onClick={onWithdraw} disabled={checkValidity()}>
              {BUTTON_CONSTANTS.WITHDRAW_SHIP_PICK_UP}
            </button>
            <button
              className="button large blue inverted"
              type="button"
              onClick={() => void onAddToMarketplace()}
              disabled={checkValidity() || isAddToMarketplaceChecking}>
              {BUTTON_CONSTANTS.ADD_TO_MARKETPLACE}
            </button>
            <button className="button small red inverted" type="button" onClick={onDiscard} disabled={checkValidity()}>
              {BUTTON_CONSTANTS.DISCARD}
            </button>
          </div>
        ) : (
          <div>
            <br />
            <br />
          </div>
        )}
      </AnimalBanner>
      {(isCheckingPendingWithdraw || isAddToMarketplaceChecking) && <DotLoader />}
      <div className="inventory-items">
        {getInventoryItems(
          [
            TABLE_HEADER_CONSTANTS.INVENTORY_TYPE,
            TABLE_HEADER_CONSTANTS.UNIT_OF_MEASURE,
            TABLE_HEADER_CONSTANTS.LOT_DATE_NO,
            isFemale ? TABLE_HEADER_CONSTANTS.FLUSH_MATING : TABLE_HEADER_CONSTANTS.QUALITY,
            isFemale ? TABLE_HEADER_CONSTANTS.EMBRYO_QTY : TABLE_HEADER_CONSTANTS.AVAILABLE_QTY,
            TABLE_HEADER_CONSTANTS.OWNED_QTY,
            TABLE_HEADER_CONSTANTS.ACTION_ITEM,
          ],
          'desktop-view',
          'Inventory Items',
        )}
        {getInventoryItems(
          [
            TABLE_HEADER_CONSTANTS.LOT_NO,
            isFemale ? TABLE_HEADER_CONSTANTS.EMBRYO_QTY : TABLE_HEADER_CONSTANTS.AVAILABLE_QTY,
            TABLE_HEADER_CONSTANTS.OWNED_QTY,
            TABLE_HEADER_CONSTANTS.ACTION_ITEM,
          ],
          'mobile-view',
          'Collection History',
        )}
      </div>

      <MergeTransactionModal
        isOpen={isOpenMergeTransactionModal}
        setIsOpen={setIsOpenMergeTransactionModal}
        pendingTransactions={pendingTransactions}
        onCreateNewOrder={() => handleCreateNewOrder()}
        onUpdateExistingOrder={(transactionId: number) => handleUpdateOrder(transactionId)}
      />

      {specimen && (
        <QuickView
          specimen={specimen}
          isOpen={isQuickViewOpen}
          onClose={() => setIsQuickViewOpen(false)}
          setIsOpen={setIsQuickViewOpen}
        />
      )}

      {accountId != null && isPayoutSetupModalOpen && (
        <StripePayoutSetupModal
          isOpen={isPayoutSetupModalOpen}
          accountId={accountId}
          mode={payoutSetupMode}
          description={MARKETPLACE_CONSTANTS.STRIPE_PAYOUT_SETUP_REQUIRED_TO_LIST}
          onClose={() => setIsPayoutSetupModalOpen(false)}
          onSuccess={() => {
            setIsPayoutSetupModalOpen(false);
            void continueToMarketplace({ setup: false }).catch(() => showToast.error(toastMessages.SOMETHING_WENT_WRONG));
          }}
        />
      )}

      <StripePayoutReviewModal isOpen={isPayoutReviewModalOpen} onClose={() => setIsPayoutReviewModalOpen(false)} />

      {accountId != null && isDocumentModalOpen && (
        <StripeKycDocumentModal
          isOpen={isDocumentModalOpen}
          accountId={accountId}
          accountStatus={stripeStatus}
          onClose={() => setIsDocumentModalOpen(false)}
          onSuccess={() => {
            setIsDocumentModalOpen(false);
            void continueToMarketplace({ document: false }).catch(() => showToast.error(toastMessages.SOMETHING_WENT_WRONG));
          }}
        />
      )}
    </div>
  );
};

export default InventoryItem;
